import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { BaseComponent } from '../../base/base.component';
import {
    extractPasswordRuleSettingsValue,
    generatePasswordRuleSettingsSchema,
    PasswordRuleSettingsSchema,
} from '../../../../../tools/schemas/graph/password-rule-settings.schema';
import { DirectorySetting } from 'projects/angular-clarity/src/app/stores/client/graph/directory-settings/actions';

@Component({
    selector: 'app-baseline',
    templateUrl: './baseline.component.html',
    styleUrls: ['./baseline.component.scss', '../../styles/baseline-style.scss'],
})
export class BaselineComponent extends BaseComponent implements OnInit {
    data: DirectorySetting;

    bannedPasswordModes = [
        { label: 'Enforce', value: 'Enforce' },
        { label: 'Audit', value: 'Audit' },
    ];

    /** Minimum length required for each word added to the custom banned-password list. */
    readonly bannedPasswordMinLength = 4;

    /** Validation message shown under the banned-password input when the last entry was rejected. */
    bannedPasswordInputError = '';

    constructor(private readonly fb: FormBuilder) {
        super();
    }

    /**
     * The banned-password list is stored on the form as a newline-separated string (to keep the
     * schema serialization unchanged). Expose it as an array of words for rendering chips.
     */
    get bannedPasswordWords(): string[] {
        const value = (this.form?.get('BannedPasswordList')?.value as string) ?? '';
        return value
            .split('\n')
            .map((word) => word.trim())
            .filter((word) => word.length > 0);
    }

    /**
     * Add a word to the custom banned-password list when the user presses Enter (or blurs the input).
     * Each word must be at least {@link bannedPasswordMinLength} characters; shorter or duplicate
     * entries are rejected and the input is left untouched so the user can correct it.
     */
    addBannedPassword(event: MatChipInputEvent): void {
        const word = (event.value ?? '').trim();

        if (!word) {
            event.chipInput?.clear();
            return;
        }

        if (word.length < this.bannedPasswordMinLength) {
            this.bannedPasswordInputError = `Each word must be at least ${this.bannedPasswordMinLength} characters.`;
            return;
        }

        const words = this.bannedPasswordWords;
        if (words.some((existing) => existing.toLowerCase() === word.toLowerCase())) {
            this.bannedPasswordInputError = 'This word is already in the list.';
            return;
        }

        words.push(word);
        this.setBannedPasswordWords(words);
        this.bannedPasswordInputError = '';
        event.chipInput?.clear();
    }

    /** Remove a word from the custom banned-password list. */
    removeBannedPassword(word: string): void {
        this.setBannedPasswordWords(this.bannedPasswordWords.filter((existing) => existing !== word));
    }

    private setBannedPasswordWords(words: string[]): void {
        const control = this.form.get('BannedPasswordList');
        control?.setValue(words.join('\n'));
        control?.markAsTouched();
        control?.updateValueAndValidity();
    }

    ngOnInit(): void {
        const schema = this.baseline?.schema as PasswordRuleSettingsSchema;

        const isBannedPasswordCheckEnabled =
            extractPasswordRuleSettingsValue(schema, 'EnableBannedPasswordCheck') === 'True';

        this.form = this.fb.group({
            BannedPasswordCheckOnPremisesMode: [
                extractPasswordRuleSettingsValue(schema, 'BannedPasswordCheckOnPremisesMode') ?? 'Enforce',
            ],
            EnableBannedPasswordCheckOnPremises: [
                extractPasswordRuleSettingsValue(schema, 'EnableBannedPasswordCheckOnPremises') === 'True',
            ],
            EnableBannedPasswordCheck: [
                extractPasswordRuleSettingsValue(schema, 'EnableBannedPasswordCheck') === 'True',
            ],
            LockoutDurationInSeconds: [
                Number(extractPasswordRuleSettingsValue(schema, 'LockoutDurationInSeconds') ?? 900),
                [Validators.required, Validators.min(5), Validators.max(18000)],
            ],
            LockoutThreshold: [
                Number(extractPasswordRuleSettingsValue(schema, 'LockoutThreshold') ?? 10),
                [Validators.required, Validators.min(1), Validators.max(50)],
            ],
            BannedPasswordList: [
                extractPasswordRuleSettingsValue(schema, 'BannedPasswordList')?.replaceAll('\t', '\n') ?? '',
                isBannedPasswordCheckEnabled ? [Validators.required] : [],
            ],
        });

        // The custom banned-password list is required only while the cloud banned-password
        // check is enabled. Keep the validator in sync as the toggle changes.
        this.form.get('EnableBannedPasswordCheck')?.valueChanges.subscribe((enabled) => {
            const listControl = this.form.get('BannedPasswordList');
            listControl?.setValidators(enabled ? [Validators.required] : []);
            // Surface the field error immediately when the check is turned on with an empty list.
            if (enabled) {
                listControl?.markAsTouched();
            }
            listControl?.updateValueAndValidity({ emitEvent: false });
        });

        this.form.valueChanges.subscribe((value) => {
            const nextSchema = generatePasswordRuleSettingsSchema({
                BannedPasswordCheckOnPremisesMode: value.BannedPasswordCheckOnPremisesMode ?? 'Enforce',
                EnableBannedPasswordCheckOnPremises: !!value.EnableBannedPasswordCheckOnPremises,
                EnableBannedPasswordCheck: !!value.EnableBannedPasswordCheck,
                LockoutDurationInSeconds: Number(value.LockoutDurationInSeconds ?? 900),
                LockoutThreshold: Number(value.LockoutThreshold ?? 10),
                BannedPasswordList: value.BannedPasswordList ?? '',
            });

            if (this.form.invalid) {
                this.baselineErrorChange.next({
                    remediate: true,
                    save: true,
                });
                return;
            } else {
                this.baselineErrorChange.next({
                    remediate: false,
                    save: false,
                });
            }

            this.baselineChange.next({
                ...this.baseline,
                schema: JSON.parse(JSON.stringify(nextSchema)),
            });
        });
    }

    private findValue(name: string): string | undefined {
        return this.data?.values?.find((v) => v.name === name)?.value;
    }

    /**
     * The desired value for a field, normalised to the same string representation Graph uses for the
     * current/actual value (mirrors {@link generatePasswordRuleSettingsSchema}) so the two can be
     * compared directly.
     */
    private desiredValue(name: string): string | undefined {
        const form = this.form;
        if (!form) return undefined;
        switch (name) {
            case 'BannedPasswordCheckOnPremisesMode':
                // Only relevant while on-premises protection is enabled (mirrors schema omission);
                // otherwise treat it as matching so it is never flagged as deviating.
                return form.get('EnableBannedPasswordCheckOnPremises')?.value
                    ? (form.get('BannedPasswordCheckOnPremisesMode')?.value ?? '')
                    : this.findValue('BannedPasswordCheckOnPremisesMode');
            case 'EnableBannedPasswordCheckOnPremises':
                return form.get('EnableBannedPasswordCheckOnPremises')?.value ? 'True' : 'False';
            case 'EnableBannedPasswordCheck':
                return form.get('EnableBannedPasswordCheck')?.value ? 'True' : 'False';
            case 'LockoutDurationInSeconds':
                return String(form.get('LockoutDurationInSeconds')?.value);
            case 'LockoutThreshold':
                return String(form.get('LockoutThreshold')?.value);
            case 'BannedPasswordList':
                // The list is stored newline-separated in the form but Graph stores it tab-separated.
                return form.get('EnableBannedPasswordCheck')?.value
                    ? ((form.get('BannedPasswordList')?.value as string) ?? '').replaceAll('\n', '\t')
                    : '';
            default:
                return undefined;
        }
    }

    /** A field deviates when the tenant's current (actual) value differs from the desired value. */
    private isDeviating(name: string): boolean {
        return (this.findValue(name) ?? '') !== (this.desiredValue(name) ?? '');
    }

    // Deviation flags (per field)
    get bannedPasswordCheckOnPremisesModeDeviated(): boolean {
        return this.isDeviating('BannedPasswordCheckOnPremisesMode');
    }
    get enableBannedPasswordCheckOnPremisesDeviated(): boolean {
        return this.isDeviating('EnableBannedPasswordCheckOnPremises');
    }
    get enableBannedPasswordCheckDeviated(): boolean {
        return this.isDeviating('EnableBannedPasswordCheck');
    }
    get lockoutDurationInSecondsDeviated(): boolean {
        return this.isDeviating('LockoutDurationInSeconds');
    }
    get lockoutThresholdDeviated(): boolean {
        return this.isDeviating('LockoutThreshold');
    }
    get bannedPasswordListDeviated(): boolean {
        return this.isDeviating('BannedPasswordList');
    }

    // Current values
    private toEnabledLabel(value: string | undefined): string {
        if (value === 'True') return 'Enabled';
        if (value === 'False') return 'Disabled';
        return 'Not configured';
    }

    get bannedPasswordCheckOnPremisesModeCurrentValue(): string {
        return this.findValue('BannedPasswordCheckOnPremisesMode') ?? 'Not configured';
    }
    get enableBannedPasswordCheckOnPremisesCurrentValue(): string {
        return this.toEnabledLabel(this.findValue('EnableBannedPasswordCheckOnPremises'));
    }
    get enableBannedPasswordCheckCurrentValue(): string {
        return this.toEnabledLabel(this.findValue('EnableBannedPasswordCheck'));
    }
    get lockoutDurationInSecondsCurrentValue(): string {
        return this.findValue('LockoutDurationInSeconds') ?? 'Not configured';
    }
    get lockoutThresholdCurrentValue(): string {
        return this.findValue('LockoutThreshold') ?? 'Not configured';
    }
    get bannedPasswordListCurrentValue(): string {
        return this.findValue('BannedPasswordList') || 'Not configured';
    }
    get isEnforceCustomListEnabled(): boolean {
        return !!this.form?.get('EnableBannedPasswordCheck')?.value;
    }
    get isOnPremisesProtectionEnabled(): boolean {
        return !!this.form?.get('EnableBannedPasswordCheckOnPremises')?.value;
    }

    /**
     * True while the custom banned-password list is mandatory — the cloud banned-password check is
     * enabled but no words have been added yet. Drives the field error, the row highlight and the
     * top banner so the missing required input is impossible to miss.
     */
    get isBannedPasswordListMissing(): boolean {
        return !!this.form?.get('EnableBannedPasswordCheck')?.value && this.bannedPasswordWords.length === 0;
    }
}
